import { ISalesNudge } from 'components/internal/sales/SalesLinkTypes';
import { COMMON_LANGUAGES } from 'lib/datasets/languages';
import { INudge } from 'lib/types/nudge';
import { ExternalServiceName } from 'lib/types/partnership';
import { ScormExportFormat } from 'lib/types/scorm-export';
import { UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import {
  COMMUNICATION_PREFERENCES,
  DEFAULT_CUSTOM_CATEGORY_LABEL,
} from '../constants';
import { COUNTRY_BY_CODE } from '../datasets/countries';
import { STATES_AND_TERRITORIES_BY_CODE } from '../datasets/statesAndTerritories';
import { getIsWorkdayServiceName } from './integrationHelper';

export const prettyPrintBoolean = (bool: boolean): string =>
  bool ? 'Yes' : '—';

export const prettyPrintBooleanToDisplayStr = (bool: boolean): string =>
  bool ? 'Yes' : 'No';

export const prettyPrintOptionalStr = (
  optionalStr: string | number
): string => {
  if (typeof optionalStr === 'number') {
    return _.toString(optionalStr);
  }

  return optionalStr || '—';
};

export const prettyPrintConstant = (type: string) =>
  _.chain(type).lowerCase().startCase().value();

export const prettyPrintCustomCategoryLabel = (label: string) => {
  return _.startCase(label || DEFAULT_CUSTOM_CATEGORY_LABEL);
};

export const prettyPrintExternalService = (
  service: ExternalServiceName,
  subserviceName?: string
): string => {
  if (getIsWorkdayServiceName(service)) {
    return 'Workday';
  }
  switch (service) {
    // HRIS
    case ExternalServiceName.ADP:
      return 'ADP';
    case ExternalServiceName.BAMBOOHR:
      return 'BambooHR';
    case ExternalServiceName.DOCEBO:
      return 'Docebo';
    case ExternalServiceName.DOCEBO_IFRAME:
      return 'Docebo IFrame';
    case ExternalServiceName.GUSTO:
      return 'Gusto';
    case ExternalServiceName.LATTICE:
      return 'Lattice';
    case ExternalServiceName.RIPPLING:
      return 'Rippling';
    case ExternalServiceName.CHARTHOP:
      return 'ChartHop';
    case ExternalServiceName.MERGE:
      return subserviceName || 'Merge';
    case ExternalServiceName.PRISM:
      return 'Prism';
    case ExternalServiceName.UKG:
      return 'UKG';

    // Communications
    case ExternalServiceName.SLACK:
      return 'Slack';
    case ExternalServiceName.ZOOM:
      return 'Zoom';

    // Security Platform
    case ExternalServiceName.VANTA:
      return 'Vanta';
    default:
      return _.startCase(_.lowerCase(service));
  }
};

export const prettyPrintCountry = (countryCode: string): string =>
  _.get(COUNTRY_BY_CODE[countryCode], 'name', countryCode);

export const prettyPrintState = (stateCode: string): string =>
  _.get(STATES_AND_TERRITORIES_BY_CODE[stateCode], 'name', stateCode);

export const prettyPrintStateAndCountry = ({
  state,
  country,
}: {
  state: string;
  country: string;
}): string => {
  if (!state && country) {
    return country === COUNTRY_BY_CODE.ZZZ.code ? "INT'L" : country;
  }
  if (state && country) {
    // Example: "USA - CA"
    return country + ' - ' + state;
  }
  return '';
};

export const prettyPrintStateAndCountryTooltip = ({
  state,
  country,
}: {
  state: string;
  country: string;
}): string => {
  if (state && country) {
    return prettyPrintCountry(country) + ' - ' + prettyPrintState(state);
  }
  if (country) {
    return prettyPrintCountry(country);
  }
  return '';
};

export const prettyPrintNudgeTitle = (nudge: ISalesNudge | INudge) => {
  const nudgeTitleArray = [`ID: ${nudge.id}`];
  const countryState = prettyPrintStateAndCountry({
    state: nudge.state,
    country: nudge.country,
  });

  if (nudge.nameOverride) {
    nudgeTitleArray.push(`Name Override: ${nudge.nameOverride}`);
  }
  if (countryState) {
    nudgeTitleArray.push(`Country/State: ${countryState}`);
  }
  if (nudge.region) {
    nudgeTitleArray.push(`Region: ${nudge.region}`);
  }
  if (nudge.targetRole) {
    nudgeTitleArray.push(`Role: ${nudge.targetRole}`);
  }
  if (nudge.industry) {
    nudgeTitleArray.push(`Industry: ${nudge.industry}`);
  }
  if (nudge.nudgePersona) {
    nudgeTitleArray.push(`Persona: ${nudge.nudgePersona}`);
  }
  if (nudge.language !== COMMON_LANGUAGES.EN) {
    nudgeTitleArray.push(`Language: ${nudge.language.toUpperCase()}`);
  }
  return _.join(nudgeTitleArray, ' | ');
};

export const prettyPrintMessageService = (type: string): string => {
  switch (type) {
    case COMMUNICATION_PREFERENCES.EMAIL:
      return 'Email';
    case COMMUNICATION_PREFERENCES.SLACK:
      return 'Slack';
    case COMMUNICATION_PREFERENCES.ZOOM:
      return 'Zoom Chat';
    default:
      return _.startCase(_.lowerCase(type));
  }
};

/**
 * Converts list of notification preference strings into a comma-separated list.
 *
 * Example:
 *   ['EMAIL', 'ZOOM'] -> 'Email, Zoom Chat'
 *
 * @param {string[]}
 * @returns {string}
 */
export const prettyPrintNotificationPreferencesFromStringList = _.flowRight([
  _.partialRight(_.join, ', '),
  _.partialRight(_.map, prettyPrintMessageService),
]);

/**
 * Converts list of notification preferences into a comma-separated list.
 *
 * Example:
 *   [
 *     { messageType: 'EMAIL', isOptedIn: true },
 *     { messageType: 'SLACK', isOptedIn: true },
 *     { messageType: 'ZOOM', isOptedIn: false }
 *   ] -> 'Email, Slack'
 *
 * @param {Object[]}
 * @returns {string}
 */
export const prettyPrintNotificationPreferencesFromObjectList = _.flowRight([
  _.partialRight(_.join, ', '),
  _.partialRight(_.map, ({ messageType }) =>
    prettyPrintMessageService(messageType)
  ),
  _.partialRight(_.filter, 'isOptedIn'),
]);

/**
 * Takes a number and returns the abbreviated number w/ `k` if >= 1000
 * returns number as is if < 1000
 *
 * Example:
 *  1234 -> 1.2k
 *  123 -> 123
 *
 *
 */
export const prettyPrintThousands = (x: number): string | number => {
  const roundedThousand = _.round(x / 1000, 1);
  const isGreaterThanOneThousand = roundedThousand >= 1;
  return isGreaterThanOneThousand ? `${roundedThousand}k` : x;
};

export const prettyPrintMillions = (x: number): string | number => {
  if (x < 1000000) {
    return prettyPrintThousands(x);
  }

  const roundedMillion = _.round(x / 1000000, 1);
  return `${roundedMillion}M`;
};

export const prettyPrintUserRole = (role: UserProfileRole): string => {
  switch (role) {
    case UserProfileRole.MANAGER:
      return 'Manager';
    case UserProfileRole.NONMANAGER:
      return 'Non-Manager';
  }
};

export const prettyPrintScormExportFormat = (format: ScormExportFormat) => {
  switch (format) {
    case ScormExportFormat.SCORM_2004_3:
      return 'SCORM 2004 3rd Edition';
    case ScormExportFormat.SCORM_2004_4:
      return 'SCORM 2004 4th Edition';
    case ScormExportFormat.SCORM_1_2:
      return 'SCORM 1.2';
    case ScormExportFormat.AICC:
      return 'AICC';
    case ScormExportFormat.CMI5:
      return 'cmi5';
    case ScormExportFormat.XAPI:
      return 'xAPI (Tin Can)';
  }
};
